import { Dispatch, SetStateAction, useMemo } from 'react';
import { Typography, Box, Button } from '@mui/material';
import { GetApp as GetAppIcon } from '@mui/icons-material';
import DisplayByToggleButton from './DisplayByToggleButton';
import { WaterlineTestReport } from '../WaterlineReports';
import { DisplayResultsBy, OpenLogResultModal, Room, WaterlineTestResult } from '../types';
import TestResultsTableContainer from './TestResultsTableContainer';
import { groupTestsBy, sortGroupKeysBy } from '../utils';
import useCustomer from '../../../../hooks/useCustomer';
import AddResultButton from './AddResultButton';
import useWaterlinePermissions from '../../../../hooks/useWaterlinePermissions';

type TestResults = {
  testResults: WaterlineTestResult[],
  getTestResults: () => void,
  displayResultsBy: DisplayResultsBy,
  setDisplayResultsBy: (displyBy: DisplayResultsBy) => void,
  rooms: Room[],
  getCompliance: () => void,
  hasInOfficeTesting: boolean,
  setTabIndex: Dispatch<SetStateAction<number>>,
  setOpenModal: Dispatch<SetStateAction<OpenLogResultModal>>,
}

const TestResults = (props: TestResults) => {
  const customer = useCustomer()
  const waterLinePermissions = useWaterlinePermissions()

  const {
    testResults,
    getTestResults,
    displayResultsBy,
    setDisplayResultsBy,
    rooms,
    getCompliance,
    setTabIndex,
    hasInOfficeTesting,
    setOpenModal,
  } = props;

  const groups = useMemo(() => {
    return groupTestsBy(displayResultsBy, testResults)
  }, [displayResultsBy, testResults])  

  const sortedKeys = useMemo(() => {
    return sortGroupKeysBy(displayResultsBy, groups)
  }, [groups])

  return (
    <Box>
      <Box sx={{ marginBottom: '15px', display: 'flex', justifyContent: 'space-between' }}>
        <Box>
          <DisplayByToggleButton
            setDisplayResultsBy={setDisplayResultsBy}
            displayResultsBy={displayResultsBy}
          />
        </Box>
        <Box>
          {
            hasInOfficeTesting
              && waterLinePermissions.inOffice.add
              && <AddResultButton setOpenModal={setOpenModal} />
          }
          <Button onClick={() => WaterlineTestReport(testResults, customer)} startIcon={<GetAppIcon />} variant="contained">
            Export All
          </Button>
        </Box>
      </Box>
      {
        sortedKeys
          .map((groupKey, index) => {
            const key = `test-results-wrapper-${index}`
            const testResults = groups.get(groupKey) ?? []

            return (
              <TestResultsTableContainer
                key={key}
                testResults={testResults}
                displayResultsBy={displayResultsBy}
                getTestResults={getTestResults}
                rooms={rooms}
                getCompliance={getCompliance}
                customer={customer}
                setTabIndex={setTabIndex}
                setOpenModal={setOpenModal}
              />
            )
          })
      }
      <Box sx={{ border: '1px solid #e0e0e0' }}>
        <Typography
          style={{
            fontSize: '13px',
            fontWeight: 'bold',
            padding: '16px 16px 16px 46px'
          }}
        >
          Notes: A failed test occurs when there is 500CFL/mL or greater of HPC.<br />
          Total Dissolved Solids (TDS): Inorganic salts and some organic matter that is dissolved in water. <br />
          pH: A measure of the acidity or alkalinity of a solution. Method check and control are added in by the lab for quality control purposes.
        </Typography>
      </Box>
    </Box>
  )
}

export default TestResults
